import { useEffect, useState } from 'react';
import { Download, Eye, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';
import type { BroadcastDeliveryStatus, BroadcastRecipientKind } from '@/types/messaging';
import { notificationsApi } from '../api/notifications.api';
import { RowsPerPageSelect } from '@/components/ui/RowsPerPageSelect';
import { DELIVERY_STATUS_LABEL } from '@/constants/messaging';

interface NotificationRecipientTableProps {
  broadcastUuid: string;
}

/** What to print in the Relation column when nothing more specific was stored. */
const KIND_LABEL: Record<BroadcastRecipientKind, string> = {
  STUDENT: 'Student',
  PARENT: 'Parent',
  TEACHER: 'Teacher',
  STAFF: 'Staff',
};

const FILTERS: readonly ('ALL' | BroadcastDeliveryStatus)[] = [
  'ALL',
  'DELIVERED',
  'FAILED',
  'PENDING',
];

/**
 * Who one send actually went to.
 *
 * Paged on the server rather than in the browser: a whole-institute broadcast is
 * a thousand rows, and every one of them carries a person's name and a masked
 * number. Fetching all of them so the page can slice ten would put the lot in
 * the browser's memory for no gain (CLAUDE.md §10).
 *
 * The numbers arrive masked — `••••••3210`. The office is checking *which*
 * number was used, and a full roll of parents' mobiles is exactly what should
 * never be readable off a screen (§9). That is also why searching is by name
 * only: the column is ciphertext on the server and cannot be matched against.
 */
export function NotificationRecipientTable({ broadcastUuid }: NotificationRecipientTableProps) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'ALL' | BroadcastDeliveryStatus>('ALL');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  // Typing should not fire a request per keystroke.
  const debouncedSearch = useDebouncedValue(search, 300);

  // A filter change with the reader on page 4 would otherwise ask for a page
  // the narrowed result does not have, and show an empty table.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  const { data, isLoading, error, refetch } = useApiQuery(
    () =>
      notificationsApi.recipients(broadcastUuid, {
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      }),
    [broadcastUuid, currentPage, pageSize, debouncedSearch, statusFilter],
  );

  const rows = data?.items ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + rows.length, totalItems);

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-6  shadow-xs flex flex-col gap-5">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 ">
        <div>
          <h3 className="text-xl font-extrabold text-slate-700 dark:text-white tracking-tight">
            Recipient list
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
            All recipients and their delivery status
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Search box */}
          <div className="relative min-w-[220px] flex-1 sm:flex-initial">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by recipient name..."
              aria-label="Search recipients by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full !h-9 !py-1 !pl-9 !pr-3.5 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !rounded-xl !bg-white dark:!bg-[#111c2e] !border border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors focus:outline-none placeholder:text-slate-400"
            />
          </div>

          {/* Filter button */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="!h-9 !py-1 !px-3.5 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors cursor-pointer"
            >
              <Filter className="h-3.5 w-3.5 text-slate-400" />
              <span>Filter</span>
              {statusFilter !== 'ALL' && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
            </button>

            {showFilterMenu && (
              <div className="absolute right-0 mt-1.5 w-44 bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-30 p-1.5 text-xs font-bold">
                {FILTERS.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => {
                      setStatusFilter(st);
                      setShowFilterMenu(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                      statusFilter === st
                        ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-extrabold'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {st === 'ALL' ? 'All statuses' : DELIVERY_STATUS_LABEL[st].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Export button */}
          <button
            type="button"
            disabled
            title="Exporting the delivery log is not available yet"
            className="!h-9 !py-1 !px-3.5 !text-[13px] font-bold border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-200 hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-300 shadow-2xs rounded-xl flex items-center gap-1.5 shrink-0 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-3.5 w-3.5 text-slate-400" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Recipient Table */}
      <div className="relative overflow-x-auto table-scrollbar rounded-2xl border border-slate-200 dark:border-slate-800">
        <table className="w-full text-left border-collapse min-w-[750px]">
          <thead>
            <tr className="bg-slate-100 dark:bg-[#0b1322] border-b border-slate-200 dark:border-slate-800 text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th className="py-3.5 px-4 w-16">S.No.</th>
              <th className="py-3.5 px-4">Recipient name</th>
              <th className="py-3.5 px-4">Mobile number</th>
              <th className="py-3.5 px-4">Relation</th>
              <th className="py-3.5 px-4">Status</th>
              <th className="py-3.5 px-4">Delivered on</th>
              <th className="py-3.5 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800/80 text-sm">
            {rows.map((item, index) => (
              <tr
                key={item.uuid}
                className="hover:bg-slate-50/80 dark:hover:bg-slate-900/40 transition-colors"
              >
                <td className="py-2 px-5 text-slate-600 dark:text-slate-400 font-mono text-[13px]">
                  {startIndex + index + 1}
                </td>
                <td className="py-2 px-5 font-black text-slate-700 dark:text-white text-[13px]">
                  {item.name}
                </td>
                <td className="py-2 px-5 text-slate-600 dark:text-slate-300 font-bold text-[13px]">
                  {item.mobile}
                </td>
                <td className="py-2 px-5 text-slate-700 dark:text-slate-200 font-bold text-[13px]">
                  {item.relationLabel ?? KIND_LABEL[item.kind]}
                </td>
                <td className="py-2 px-5 whitespace-nowrap">
                  <Badge tone={DELIVERY_STATUS_LABEL[item.status].tone}>
                    {DELIVERY_STATUS_LABEL[item.status].label}
                  </Badge>
                </td>
                <td className="py-2 px-5 text-slate-600 dark:text-slate-300 font-bold text-[13px] whitespace-nowrap">
                  {item.deliveredAt ? formatDateTime(item.deliveredAt) : '—'}
                </td>
                <td className="py-2 px-5 text-right whitespace-nowrap">
                  <button
                    type="button"
                    title="View details"
                    onClick={() =>
                      item.status === 'FAILED'
                        ? toast.error(
                            `${item.name} — not delivered`,
                            item.failureReason ?? 'The provider gave no reason.',
                          )
                        : toast.info(
                            `${item.name} (${item.relationLabel ?? KIND_LABEL[item.kind]})`,
                            `${DELIVERY_STATUS_LABEL[item.status].label}${
                              item.deliveredAt ? ` on ${formatDateTime(item.deliveredAt)}` : ''
                            } — ${item.mobile}`,
                          )
                    }
                    className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/60 transition-colors cursor-pointer rounded-lg border-0 bg-transparent"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}

            {isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-6 px-5">
                  <SkeletonLines count={4} />
                </td>
              </tr>
            )}

            {error && !isLoading && (
              <tr>
                <td colSpan={7} className="py-10 text-center">
                  <p className="text-sm font-bold text-slate-600 dark:text-slate-300">
                    {error.message}
                  </p>
                  <button
                    type="button"
                    onClick={refetch}
                    className="mt-2 text-sm font-extrabold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
                  >
                    Try again
                  </button>
                </td>
              </tr>
            )}

            {!isLoading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="py-10 text-center text-slate-400 font-medium">
                  No recipients found matching the filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500 dark:text-slate-400 font-medium pt-2">
        <span>
          Showing {totalItems === 0 ? 0 : startIndex + 1} to {endIndex} of {totalItems} entries
        </span>

        <div className="flex items-center gap-2">
          {/* Per page selector */}
          <RowsPerPageSelect
            className="mr-2"
            value={pageSize}
            options={[8, 10, 20]}
            onChange={setPageSize}
          />

          {/* Page numbers & arrows */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              aria-label="Previous page"
              className="h-8 w-8 rounded-lg bg-white dark:bg-[#111c2e] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none cursor-pointer transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setCurrentPage(pageNum)}
                aria-current={pageNum === currentPage ? 'page' : undefined}
                className={`h-8 w-8 rounded-lg font-black text-xs flex items-center justify-center transition-colors cursor-pointer ${
                  pageNum === currentPage
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-white dark:bg-[#111c2e] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50'
                }`}
              >
                {pageNum}
              </button>
            ))}

            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              aria-label="Next page"
              className="h-8 w-8 rounded-lg bg-white dark:bg-[#111c2e] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none cursor-pointer transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
